// Dominoes on the table in 3D: the line of play snaking across the felt, each
// player's tiles standing on edge in front of them (you see your faces,
// everyone else sees backs), the hands laid face up when a hand ends, seat
// labels and the scoreboard. Table-local, floor origin (see GameTable3D).
// The tile model itself is in DominoTile.tsx.

import React, { useMemo } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { TABLE_TOP, seatXZ } from '../tableLayout';
import { handOf } from '../gameStore';
import { SEAT_NAMES, type DominoesState } from '../../../../supabase/functions/games/dominoes.ts';
import { vrColors as colors, noRaycast } from '../../VR/vrUi';
import { DominoGroup, TILE, type TilePlacement } from './DominoTile';
import { layoutLine } from './layout';
import { tilePips } from './pips';
import type { Table3DProps } from '../types';

const HAND_R = 0.5;        // upright tiles this far from the centre, in front of each seat
const REVEAL_R = 0.47;
const Y = TABLE_TOP + 0.0005;

/** Unit vector from the centre toward seat i, and the tangent to its right. */
function seatFrame(i: number) {
  const [x, z] = seatXZ(i, 1);
  return { ux: x, uz: z, tx: -z, tz: x };
}

/** Name of a side for the scoreboard. */
export function sideName(g: DominoesState, side: number, st?: { seats: ({ name: string } | null)[] }) {
  if (g.mode === 'partners') return side === 0 ? 'N/S' : 'E/W';
  const s = g.sides[side][0];
  return st?.seats[s]?.name ?? SEAT_NAMES[s];
}

export function DominoesTable3D({ def, table, seat, entry }: Table3DProps) {
  const st = table?.state;
  const g = (st?.g ?? null) as DominoesState | null;
  const mine = handOf(entry);
  const showing = !!g && st?.phase !== 'lobby';

  // The line of play (one merged mesh).
  const line = useMemo<TilePlacement[]>(() => {
    if (!g || !showing) return [];
    return layoutLine(g.line, g.first).map((t) => ({
      a: t.inner, b: t.outer, position: [t.x, Y, t.z], rotation: [0, t.yaw, 0],
    }));
  }, [g?.line, g?.first, showing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hands: on edge while playing; laid face up when the hand is over.
  const handKey = g ? `${g.handNo}|${g.phase}|${g.counts.join(',')}|${seat}|${mine.join(',')}` : '';
  const hands = useMemo<TilePlacement[]>(() => {
    if (!g || !showing) return [];
    const out: TilePlacement[] = [];
    const reveal = g.phase !== 'playing' && g.lastHand ? g.lastHand.reveal : null;
    for (const s of g.players) {
      const f = seatFrame(s);
      const tiles: (string | null)[] = reveal ? reveal[s] : s === seat ? mine : Array(g.counts[s]).fill(null);
      const n = tiles.length;
      if (!n) continue;
      const step = Math.min(TILE.W + (reveal ? 0.006 : 0.004), 0.44 / n);
      tiles.forEach((t, k) => {
        const off = (k - (n - 1) / 2) * step;
        const [a, b] = t ? tilePips(t) : [null, null];
        if (reveal) {
          const r = REVEAL_R;
          out.push({ a, b, position: [f.ux * r + f.tx * off, Y, f.uz * r + f.tz * off], rotation: [0, Math.atan2(f.ux, f.uz), 0] });
        } else {
          // Long axis up, face toward the seat: tip the tile (X), then turn it to the seat (Y).
          // The seat sees its own tiles left-to-right as listed (its right = −tangent).
          const o = -off;
          out.push({
            a, b, order: 'YXZ',
            position: [f.ux * HAND_R + f.tx * o, TABLE_TOP + TILE.L / 2, f.uz * HAND_R + f.tz * o],
            rotation: [-Math.PI / 2, Math.atan2(-f.ux, -f.uz), 0],
          });
        }
      });
    }
    return out;
  }, [handKey, showing]); // eslint-disable-line react-hooks/exhaustive-deps

  // End markers so "left" / "right" on the panels map onto the table.
  const laid = useMemo(() => (g && showing && g.line.length ? layoutLine(g.line, g.first) : []), [g?.line, g?.first, showing]); // eslint-disable-line react-hooks/exhaustive-deps
  const ends = g && laid.length ? [
    { label: `L ${g.line[0].l}`, t: laid[0] },
    { label: `R ${g.line[g.line.length - 1].r}`, t: laid[laid.length - 1] },
  ] : [];

  let board = 'Loading…';
  if (st && g && showing) {
    board = g.sides.map((_, i) => `${sideName(g, i, st)} ${g.scores[i]}`).join('  ·  ') + `  —  to ${g.target}`;
  } else if (st) {
    const n = st.seats.filter(Boolean).length;
    board = `${n}/4 seated — 4 play partners, 2 head-to-head`;
  }
  const lh = g?.lastHand;
  const handLine = g && lh && g.phase !== 'playing'
    ? lh.side === null ? 'Blocked — tied, no score'
      : `${sideName(g, lh.side, st)} ${lh.how === 'domino' ? 'domino' : 'win the block'} +${lh.points}`
    : '';

  return (
    <>
      <DominoGroup tiles={line} />
      <DominoGroup tiles={hands} />
      {ends.map((e, i) => (
        <Billboard key={i} position={[e.t.x, TABLE_TOP + 0.07, e.t.z]}>
          <Text fontSize={0.03} color={colors.teal} outlineWidth={0.003} outlineColor="#000" raycast={noRaycast}>{e.label}</Text>
        </Billboard>
      ))}
      {/* Seat labels */}
      {[0, 1, 2, 3].map((i) => {
        const s = st?.seats[i];
        const [x, z] = seatXZ(i, 1.15);
        const playing = !!g && showing && g.players.includes(i);
        const turn = playing && g!.phase === 'playing' && g!.turn === i && st?.phase === 'playing';
        const last = g?.last && g.last.seat === i && g.phase === 'playing'
          ? g.last.kind === 'pass' ? ' · passed' : g.last.kind === 'draw' ? ` · drew ${g.last.drew}` : '' : '';
        const line2 = playing ? `${g!.counts[i]} tile${g!.counts[i] === 1 ? '' : 's'}${last}` : '';
        if (!s && showing) return null;
        return (
          <Billboard key={i} position={[x, 1.45, z]}>
            <Text fontSize={0.085} color={turn ? colors.teal : '#ffffff'} outlineWidth={0.006} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
              {s ? `${s.name}${s.kind === 'bot' ? ' 🤖' : ''}${i === seat ? ' (you)' : ''}` : `${SEAT_NAMES[i]} — open`}
            </Text>
            {line2 && <Text position={[0, -0.02, 0]} fontSize={0.065} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">{line2}</Text>}
          </Billboard>
        );
      })}
      {/* Scoreboard over the table */}
      <Billboard position={[0, 1.75, 0]}>
        <Text fontSize={0.11} color="#ffffff" outlineWidth={0.007} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
          {g && showing && g.boneyard > 0 ? `${def.label} · boneyard ${g.boneyard}` : def.label}
        </Text>
        <Text position={[0, -0.02, 0]} fontSize={0.075} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">
          {board}
        </Text>
        {(handLine || (st?.phase === 'over' && g?.winner !== null && g)) && (
          <Text position={[0, -0.13, 0]} fontSize={0.08} color={colors.teal} outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">
            {st?.phase === 'over' && g && g.winner !== null ? `${sideName(g, g.winner, st)} win${g.mode === 'solo' ? 's' : ''} the game!` : handLine}
          </Text>
        )}
      </Billboard>
    </>
  );
}

export default DominoesTable3D;
